
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './styles/FlashSaleCards.css';
import flashSaleService from '../../services/flashsale.service';
import { FlashSaleProduct } from '../../dto/flashsale.dto';
import { useCart } from '../../../../contexts/CartContext';
import { CART_CONSTANTS } from '../../../../shared/components/FuncationsCartAndWishlist/Dto/AddToCardtUtilityDto';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import LoadingSpinner from '../../../../shared/components/LodingSpiner/LodingSpiner';

const FlashSaleCards: React.FC = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState<FlashSaleProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { addToCart, cartItems, updateQuantity, removeFromCart } = useCart();

  const getTruncatedTitle = (title?: string, maxWords: number = 6) => {
    const safeTitle = (title || '').trim() || 'Item';
    const words = safeTitle.split(/\s+/);
    if (words.length <= maxWords) return safeTitle;
    return words.slice(0, maxWords).join(' ') + '...';
  };

  useEffect(() => {
    fetchFlashSales();
  }, []);

  const fetchFlashSales = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await flashSaleService.getFlashSales();
      if (response.success && response.data) {
        setProducts(response.data);
      } else {
        setError('Failed to load flash sale products');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while fetching flash sale products');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (_product: FlashSaleProduct) => {
    try {
      addToCart(_product, 1);
      toast.success(`${getTruncatedTitle(_product.title)} added to cart!`, {
        position: 'bottom-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } catch (error) {
      toast.error('Failed to add item to cart', {
        position: 'bottom-right',
        autoClose: 3000,
      });
    }
  };


  if (loading) {
    return (
      <div className="flash-sale-cards" style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
        <LoadingSpinner size="large" />
      </div>
    );
  }
  if (error) {
    return <div className="flash-sale-cards">{error}</div>;
  }

  return (
    <div className="flash-sale-cards">
      {products.map((product) => {
        const cartItem = cartItems.find((item) => {
          if (item.productId !== product._id) return false;
          return !item.variantSku || item.variantSku === '';
        });

        const isInCart = !!cartItem;
        const cartQuantity = cartItem?.quantity || 0;

        const handleCartQuantityChange = (newQuantity: number) => {
          if (!cartItem) return;

          if (newQuantity <= 0) {
            removeFromCart(cartItem.productId, cartItem.variantSku, cartItem.variantId);
          } else {
            updateQuantity(cartItem.productId, newQuantity, cartItem.variantSku, cartItem.variantId);
          }
        };

        const handleDecreaseClick = (e: React.MouseEvent<HTMLButtonElement>) => {
          e.preventDefault();
          e.stopPropagation();
          handleCartQuantityChange(cartQuantity - 1);
        };

        const handleIncreaseClick = (e: React.MouseEvent<HTMLButtonElement>) => {
          e.preventDefault();
          e.stopPropagation();
          if (cartQuantity < CART_CONSTANTS.MAX_QUANTITY) {
            handleCartQuantityChange(cartQuantity + 1);
          }
        };

        const handleAddClick = (e: React.MouseEvent<HTMLButtonElement>) => {
          e.preventDefault();
          e.stopPropagation();
          handleAddToCart(product);
        };

        return (
          <div 
            key={product._id} 
            className="flash-sale-card"
            onClick={() => navigate(`/product/${product.slug || product._id}`)}
            style={{ cursor: 'pointer' }}
          >
            <div className="flash-sale-card__badge">Sale</div>
            <div className="flash-sale-card__image-container">
              <img 
                src={product.imageUrl || 'https://ik.imagekit.io/raghav11/download.jpg?updatedAt=1757934662868'} 
                alt={product.title}
                className="flash-sale-card__image"
              />
            </div>
            <div className="flash-sale-card__content">
              <h3 className="flash-sale-card__name">{product.title}</h3>
              <div className="flash-sale-card__price">₹ {Math.round(product.price)}</div>
              <div className="flash-sale-card__stock">Limited Stock</div>
              <div className="flash-sale-card__buttons">
                {isInCart ? (
                  <div className="flash-sale-card__add-cart cart-quantity-controls">
                    <button
                      type="button"
                      className="cart-quantity-btn"
                      onClick={handleDecreaseClick}
                      aria-label="Decrease quantity"
                    >
                      -
                    </button>
                    <span className="cart-quantity-display">{cartQuantity}</span>
                    <button
                      type="button"
                      className="cart-quantity-btn"
                      onClick={handleIncreaseClick}
                      aria-label="Increase quantity"
                      disabled={cartQuantity >= CART_CONSTANTS.MAX_QUANTITY}
                    >
                      +
                    </button>
                  </div>
                ) : (
                  <button 
                    type="button"
                    className="flash-sale-card__add-cart"
                    onClick={handleAddClick}
                    aria-label="Add to cart"
                  >
                    Add to Cart
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default FlashSaleCards;
